/**
 * Bike Page Content Component - Main Category Page Orchestrator
 * 
 * The primary component that orchestrates entire bike category pages (MTB, Road, Gravel)
 * with tabbed navigation, hero sections, and integrated product listings.
 * Features comprehensive content management and responsive interactions.
 * 
 * Page Architecture:
 * - Hero section with bike category branding
 * - Tabbed content navigation (Description, Features, Geometry, etc.)
 * - Product grid integration for category products
 * - Customer reviews section
 * - Image modal for full-screen viewing
 * 
 * Tab Management:
 * - Dynamic tab content loading with Suspense boundaries
 * - Sticky navigation behavior with scroll detection
 * - Deep linking support for direct tab access
 * - Smooth scrolling between sections
 * - Mobile-optimized tab switching
 * 
 * Content Integration:
 * - Sanity CMS integration for all page content
 * - Fallback content for missing CMS data
 * - Dynamic image handling with error recovery
 * - Rich text content rendering via Portable Text
 * 
 * Responsive Features:
 * - Mobile-first responsive design
 * - Touch-optimized navigation and interactions
 * - Adaptive layout for different screen sizes
 * - Progressive content loading for performance
 * 
 * Performance Optimizations:
 * - Dynamic component imports for code splitting
 * - Lazy loading of tab content
 * - Image optimization and lazy loading
 * - Efficient state management with hooks
 * - Memoization of expensive operations
 * 
 * User Experience:
 * - Smooth transitions between tabs and sections
 * - Visual feedback for user interactions
 * - Loading states for dynamic content
 * - Keyboard navigation support
 * - Accessibility-compliant structure
 * 
 * @param {object} pageData - Sanity CMS page data with tabs and content
 * @param {Array} products - Array of products for the category grid
 * @param {string} category - Bike category ('mtb', 'road', 'gravel')
 * @param {Array} galleryImages - Images for the pictures gallery tab
 * @param {Array} fallbackImages - Fallback images for missing content
 * @param {string} bikeName - Display name for the bike category
 * @param {string} geometryDefaultImage - Default geometry diagram image
 * @param {string} certificationDefaultImage - Default certification image
 * @param {string} certificationDefaultDescription - Default certification text
 * @param {boolean} geometryHasSubtabs - Whether geometry has race/endurance tabs
 * @param {string} geometryCategory - Category for geometry display logic
 * 
 * @description Comprehensive bike category page with tabbed navigation and product integration
 */

import { useState, useRef, useEffect, Suspense } from 'react';
import Head from 'next/head';
import BikeHeroSection from './BikeHeroSection';
import TabsContainer from './TabsContainer';
import ReviewsSection from './ReviewsSection';
import ProductGrid from './ProductGrid';
import { scrollToElement } from '../../utils/scrollUtils';
import { handlePrevImage, handleNextImage } from '../../utils/imageHandlers';
import { 
  DynamicImageModal,
  DynamicDescriptionTabContent,
  DynamicFeaturesTabContent,
  DynamicGeometryTabContent,
  DynamicCertificationsTabContent,
  DynamicPicturesTabContent
} from '../../utils/dynamicImports';

const BikePageContent = ({ 
  pageData = {}, 
  products = [], 
  category = '',
  galleryImages = [],
  fallbackImages = [],
  bikeName = 'Bike',
  geometryDefaultImage = "/images/mtb-yggy-geometry.png",
  certificationDefaultImage = "/images/certification-iso.png",
  certificationDefaultDescription = "This frame is certified by an independent laboratory according to the ISO 4210-2 standard.",
  geometryHasSubtabs = false,
  geometryCategory = "mtb"
}) => {
  const contentRef = useRef(null);
  const tabsRef = useRef(null);
  const tabContentRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [isTabsSticky, setIsTabsSticky] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Debug the page data structure
  useEffect(() => {
    console.log(`BikePageContent for ${category}:`, {
      pageTitle: pageData?.title,
      hasDescription: !!pageData?.tabs?.descriptionTab,
      hasFeatures: !!pageData?.tabs?.featuresTab,
      hasGeometry: !!pageData?.tabs?.geometryTab,
      hasCertifications: !!pageData?.tabs?.certificationsTab,
      hasPictures: !!pageData?.tabs?.picturesTab,
    });
    
    // Check if description tab has the correct data structure
    if (pageData?.tabs?.descriptionTab) {
      console.log(`${category} description tab:`, {
        hasDefaultSections: !!pageData.tabs.descriptionTab.defaultSections,
        section1: !!pageData.tabs.descriptionTab.defaultSections?.section1,
        section2: !!pageData.tabs.descriptionTab.defaultSections?.section2,
        section3: !!pageData.tabs.descriptionTab.defaultSections?.section3,
        section4: !!pageData.tabs.descriptionTab.defaultSections?.section4,
        additionalSections: pageData.tabs.descriptionTab.sections?.length || 0
      });
    }
  }, [pageData, category]);

  // Get header height on mount and window resize
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        setHeaderHeight(header.offsetHeight + 40);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Track when tabs become sticky
  useEffect(() => {
    const handleScroll = () => {
      if (tabsRef.current) {
        const tabsTop = tabsRef.current.getBoundingClientRect().top;
        const isMobileView = window.innerWidth < 768;
        
        if (isMobileView) {
          // Mobile: smooth detection with throttling to prevent flickering
          const shouldBeSticky = tabsTop <= 5;
          setIsTabsSticky(shouldBeSticky);
        } else {
          // Desktop: original simple detection
          setIsTabsSticky(tabsTop <= headerHeight);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headerHeight]);

  // Check if we're on mobile on mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Function to handle tab changes and scroll to content
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    
    // Only scroll if the tabs are in sticky mode
    if (isTabsSticky && tabContentRef.current) {
      // After state update, scroll to the top of the tab content
      setTimeout(() => {
        const tabHeight = tabsRef.current ? tabsRef.current.offsetHeight : 0;
        const yOffset = headerHeight + tabHeight;
        
        const tabContentTop = tabContentRef.current.getBoundingClientRect().top + window.scrollY;
        
        window.scrollTo({
          top: tabContentTop - yOffset - 12,
          behavior: 'smooth'
        });
      }, 10);
    }
  };

  const scrollToContent = () => {
    scrollToElement(contentRef, headerHeight);
  };

  // Tab content helpers
  const tabsData = [
    { id: 'description', label: pageData?.tabs?.descriptionTab?.title || 'Description' },
    { id: 'features', label: pageData?.tabs?.featuresTab?.title || 'Features' },
    { id: 'geometry', label: pageData?.tabs?.geometryTab?.title || 'Geometry' },
    { id: 'certifications', label: pageData?.tabs?.certificationsTab?.title || 'Certifications' },
    { id: 'pictures', label: pageData?.tabs?.picturesTab?.title || 'Pictures' }
  ];

  return (
    <>
      <Head>
        <title>{pageData?.title || `${bikeName} | WoodAlps`}</title>
        <meta
          name="description"
          content={pageData?.description || `Explore the ${bikeName} wooden bicycle by WoodAlps, a perfect blend of craftsmanship and performance.`}
        />
        <style>{`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-20px);
            }
            60% {
              transform: translateY(-10px);
            }
          }
        `}</style>
      </Head>

      {/* Hero Banner */}
      <BikeHeroSection 
        backgroundImage={pageData?.heroSection?.backgroundImage}
        title={pageData?.heroSection?.title || bikeName}
        buttonLabel={pageData?.heroSection?.buttonLabel || `Shop the ${bikeName}`}
        defaultBackgroundImage={fallbackImages?.[0]?.src || `/images/${category.toLowerCase()}-hero.jpg`}
        onButtonClick={scrollToContent}
      />

      {/* Main content container */}
      <div ref={contentRef} className="bg-gradient-to-b from-white to-wood-brown/5">
        {/* Product Grid */}
        <ProductGrid 
          products={products || []} 
          configureCard={pageData?.productGrid?.configureCard || {}} 
          category={category}
        />

        {/* Tabs Section */}
        <TabsContainer
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          tabsData={tabsData}
          headerHeight={headerHeight}
          isTabsSticky={isTabsSticky}
          tabsRef={tabsRef}
          tabContentRef={tabContentRef}
          isMobile={isMobile}
        >
          {/* Description Tab Content */}
          {activeTab === 'description' && (
            <Suspense fallback={<div className="animate-pulse h-32 bg-gray-200 rounded"></div>}>
              <DynamicDescriptionTabContent 
                descriptionTabData={pageData?.tabs?.descriptionTab} 
                category={category}
              />
            </Suspense>
          )}

          {/* Features Tab Content */}
          {activeTab === 'features' && (
            <Suspense fallback={<div className="animate-pulse h-32 bg-gray-200 rounded"></div>}>
              <DynamicFeaturesTabContent 
                features={[
                  ...(pageData?.tabs?.featuresTab?.features || []),
                  ...(pageData?.tabs?.featuresTab?.referencedFeatures || [])
                ]} 
                category={category}
              />
            </Suspense>
          )}

          {/* Geometry Tab Content */}
          {activeTab === 'geometry' && (
            <Suspense fallback={<div className="animate-pulse h-32 bg-gray-200 rounded"></div>}>
              <DynamicGeometryTabContent 
                geometryData={pageData?.tabs?.geometryTab} 
                defaultImage={geometryDefaultImage}
                hasGeometrySubtabs={geometryHasSubtabs}
                category={geometryCategory}
              />
            </Suspense>
          )}

          {/* Certifications Tab Content */}
          {activeTab === 'certifications' && (
            <Suspense fallback={<div className="animate-pulse h-32 bg-gray-200 rounded"></div>}>
              <DynamicCertificationsTabContent 
                certificationData={pageData?.tabs?.certificationsTab}
                defaultImage={certificationDefaultImage}
                defaultDescription={certificationDefaultDescription}
              />
            </Suspense>
          )}
          
          {/* Pictures Tab Content */}
          {activeTab === 'pictures' && (
            <Suspense fallback={<div className="animate-pulse h-32 bg-gray-200 rounded"></div>}>
              <DynamicPicturesTabContent 
                picturesData={pageData?.tabs?.picturesTab}
                fallbackImages={fallbackImages || []}
                isMobile={isMobile}
                setSelectedImage={setSelectedImage}
                setCurrentImageIndex={setCurrentImageIndex}
                bikeName={bikeName}
              />
            </Suspense>
          )}
        </TabsContainer>
        
        {/* Reviews Section */}
        <ReviewsSection 
          title={pageData?.reviewsSection?.title || 'Customer Reviews'}
          reviews={pageData?.reviewsSection?.reviews || []}
        />
      </div>
      
            {/* Image Modal - only shown when an image is selected and not on mobile */}
      {selectedImage && !isMobile && (
        <Suspense fallback={null}>
          <DynamicImageModal 
            image={selectedImage.src} 
            alt={selectedImage.alt}
            onClose={() => setSelectedImage(null)}
            onPrev={() => handlePrevImage(selectedImage, currentImageIndex, galleryImages, fallbackImages, setCurrentImageIndex, setSelectedImage)}
            onNext={() => handleNextImage(selectedImage, currentImageIndex, galleryImages, fallbackImages, setCurrentImageIndex, setSelectedImage)}
            showNavigation={true}
          />
        </Suspense>
      )}
    </>
  );
};

export default BikePageContent; 